import { useLayoutEffect, useState } from "react";

const VIEWPORT_MARGIN = 8;
const GAP = 4;

function estimateMenuHeight(itemCount, preferredMaxHeight, panelEl) {
  const byItems = itemCount > 0 ? itemCount * 34 + 12 : preferredMaxHeight;
  let height = Math.min(preferredMaxHeight, byItems);
  if (panelEl) {
    height = Math.min(preferredMaxHeight, Math.max(height, panelEl.scrollHeight));
  }
  return height;
}

/**
 * Chooses top vs bottom placement from viewport space around the trigger.
 */
export function useToolbarMenuPlacement({
  open,
  anchorRef,
  panelRef,
  preferredMaxHeight = 280,
  itemCount = 0,
}) {
  const [placement, setPlacement] = useState("bottom");
  const [maxHeightPx, setMaxHeightPx] = useState(preferredMaxHeight);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return undefined;

    const measure = () => {
      const anchor = anchorRef.current;
      const trigger =
        anchor.querySelector(".cms-toolbar-hover-menu__trigger") ||
        anchor.querySelector("button") ||
        anchor;
      const rect = trigger.getBoundingClientRect();
      const viewportH = window.innerHeight;

      const panelHeight = estimateMenuHeight(
        itemCount,
        preferredMaxHeight,
        panelRef.current,
      );

      const spaceBelow = viewportH - rect.bottom - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - VIEWPORT_MARGIN;

      const fitsBelow = spaceBelow >= panelHeight + GAP;
      const fitsAbove = spaceAbove >= panelHeight + GAP;

      let nextPlacement = "bottom";
      if (!fitsBelow && (fitsAbove || spaceAbove > spaceBelow)) {
        nextPlacement = "top";
      }

      const available = nextPlacement === "bottom" ? spaceBelow : spaceAbove;
      const capped = Math.max(96, Math.min(preferredMaxHeight, available - GAP));

      setPlacement(nextPlacement);
      setMaxHeightPx(capped);
    };

    measure();
    const raf = requestAnimationFrame(measure);

    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, anchorRef, panelRef, preferredMaxHeight, itemCount]);

  const panelPositionClass =
    placement === "top" ? "bottom-full pb-1" : "top-full pt-1";

  return { placement, maxHeightPx, panelPositionClass };
}
